import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Download, Loader2, Search } from 'lucide-react'
import { suporteService } from '../services/suporte'
import type { SuporteGeracaoEnvioItem, SuporteGeracaoEnvioLoteResumo } from '../tipos/geracaoEnvio'
import { useToast } from '../../../contexts/NotificationContext'
import { useUsuario } from '../../../contexts/UsuarioContext'
import { InputDate } from '../../../componentesCompartilhados/form/InputDate'

type GrupoApontamentos = {
  chave: string
  numeroTicket: string
  nomeSuporte: string
  analistaSmnNome?: string
  solicitanteNome?: string
  prefixoClockify?: string
  itens: SuporteGeracaoEnvioItem[]
}

export default function GeracaoEnvio() {
  const toast = useToast()
  const { usuario } = useUsuario()
  const isAnalista = usuario?.papeis?.includes('2') ?? false

  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [buscaDev, setBuscaDev] = useState('')
  const [desenvolvedores, setDesenvolvedores] = useState<Array<{ pessoaId: number; nome: string }>>([])
  const [desenvolvedorIdsSelecionados, setDesenvolvedorIdsSelecionados] = useState<number[]>([])
  const [itens, setItens] = useState<SuporteGeracaoEnvioItem[]>([])
  const [idsIgnorados, setIdsIgnorados] = useState<Set<number>>(new Set())
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState(false)
  const [carregandoLotes, setCarregandoLotes] = useState(false)
  const [revertendoLoteId, setRevertendoLoteId] = useState<number | null>(null)
  const [lotes, setLotes] = useState<SuporteGeracaoEnvioLoteResumo[]>([])
  const [agruparPorData, setAgruparPorData] = useState(false)
  const [dropdownDevAberto, setDropdownDevAberto] = useState(false)
  const dropdownDevRef = useRef<HTMLDivElement>(null)
  const selecaoInicialFeitaRef = useRef(false)

  useEffect(() => {
    if (!usuario?.pessoaId) return
    if (selecaoInicialFeitaRef.current) return
    setDesenvolvedorIdsSelecionados([usuario.pessoaId])
    selecaoInicialFeitaRef.current = true
  }, [usuario?.pessoaId])

  useEffect(() => {
    if (!dropdownDevAberto) return
    const handleClickFora = (e: MouseEvent) => {
      if (dropdownDevRef.current && !dropdownDevRef.current.contains(e.target as Node))
        setDropdownDevAberto(false)
    }
    document.addEventListener('click', handleClickFora)
    return () => document.removeEventListener('click', handleClickFora)
  }, [dropdownDevAberto])

  useEffect(() => {
    const carregarDesenvolvedores = async () => {
      if (!isAnalista) return
      try {
        const lista = await suporteService.listarDesenvolvedoresGeracaoEnvio()
        const listaBase = lista || []

        const listaComUsuario = (() => {
          if (!usuario?.pessoaId || !usuario?.nome) return listaBase
          const jaExiste = listaBase.some(dev => dev.pessoaId === usuario.pessoaId)
          if (jaExiste) return listaBase
          return [...listaBase, { pessoaId: usuario.pessoaId, nome: usuario.nome }]
        })()

        const listaOrdenada = (() => {
          if (!usuario?.pessoaId) {
            return [...listaComUsuario].sort((a, b) =>
              a.nome.localeCompare(b.nome, 'pt-BR'),
            )
          }

          const usuarioNaLista = listaComUsuario.find(p => p.pessoaId === usuario.pessoaId)
          const demais = listaComUsuario
            .filter(p => p.pessoaId !== usuario.pessoaId)
            .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))

          if (!usuarioNaLista) return demais
          return [usuarioNaLista, ...demais]
        })()

        setDesenvolvedores(listaOrdenada)
      } catch (error: any) {
        toast.error('Erro', error.message || 'Erro ao carregar desenvolvedores')
      }
    }
    carregarDesenvolvedores()
  }, [isAnalista, usuario?.pessoaId, usuario?.nome])

  useEffect(() => {
    if (!isAnalista) return

    let cancelado = false

    const carregarLotes = async () => {
      setCarregandoLotes(true)
      try {
        const resposta = await suporteService.listarLotesGeracaoEnvio()
        if (cancelado) return
        setLotes(resposta || [])
      } catch (error: any) {
        if (cancelado) return
        toast.error('Erro', error.message || 'Erro ao carregar lotes de geração para envio')
      } finally {
        if (cancelado) return
        setCarregandoLotes(false)
      }
    }

    carregarLotes()

    return () => {
      cancelado = true
    }
  }, [isAnalista])

  const desenvolvedoresFiltrados = useMemo(() => {
    const termo = buscaDev.trim().toLowerCase()
    if (!termo) return desenvolvedores
    return desenvolvedores.filter((d) => d.nome.toLowerCase().includes(termo))
  }, [desenvolvedores, buscaDev])

  const textoTriggerDesenvolvedores = useMemo(() => {
    if (desenvolvedorIdsSelecionados.length === 0) return 'Nenhum colaborador selecionado'
    if (desenvolvedorIdsSelecionados.length === 1) {
      const nome = desenvolvedores.find((d) => d.pessoaId === desenvolvedorIdsSelecionados[0])?.nome
      return nome ?? '1 selecionado'
    }
    return `${desenvolvedorIdsSelecionados.length} selecionados`
  }, [desenvolvedorIdsSelecionados, desenvolvedores])

  const grupos = useMemo(() => {
    const mapa = new Map<string, GrupoApontamentos>()
    const ordenados = [...itens].sort((a, b) => {
      const dataA = a.inicioMarcacao ? new Date(a.inicioMarcacao).getTime() : 0
      const dataB = b.inicioMarcacao ? new Date(b.inicioMarcacao).getTime() : 0
      return dataA - dataB
    })

    for (const item of ordenados) {
      const chave = String(item.idSuporte)
      if (!mapa.has(chave)) {
        mapa.set(chave, {
          chave,
          numeroTicket: item.numeroTicket,
          nomeSuporte: item.nomeSuporte ?? '',
          analistaSmnNome: item.analistaSmnNome,
          solicitanteNome: item.solicitanteNome,
          prefixoClockify: item.prefixoClockify,
          itens: [],
        })
      }
      mapa.get(chave)?.itens.push(item)
    }

    return Array.from(mapa.values())
  }, [itens])

  const resumoPorGrupo = useMemo(() => {
    const resumo = new Map<string, { totalMinutos: number; totalHoras: string; porResponsavel: Array<{ nome: string; minutos: number; horas: string }> }>()
    for (const grupo of grupos) {
      const itensValidos = grupo.itens.filter((i) => !idsIgnorados.has(i.idClockifyTimeEntry))
      const totalMinutos = itensValidos.reduce((acc, i) => acc + (i.minutos || 0), 0)

      const porResponsavelMap = new Map<string, number>()
      for (const item of itensValidos) {
        const nome = item.responsavelNome || '-'
        porResponsavelMap.set(nome, (porResponsavelMap.get(nome) || 0) + (item.minutos || 0))
      }

      const porResponsavel = Array.from(porResponsavelMap.entries())
        .sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'))
        .map(([nome, minutos]) => {
          const horasInt = Math.floor(minutos / 60)
          const mins = minutos % 60
          return {
            nome,
            minutos,
            horas: `${String(horasInt).padStart(2, '0')}:${String(mins).padStart(2, '0')}`,
          }
        })

      const totalHorasInt = Math.floor(totalMinutos / 60)
      const totalMins = totalMinutos % 60
      resumo.set(grupo.chave, {
        totalMinutos,
        totalHoras: `${String(totalHorasInt).padStart(2, '0')}:${String(totalMins).padStart(2, '0')}`,
        porResponsavel,
      })
    }
    return resumo
  }, [grupos, idsIgnorados])

  const temApontamentoParaGerar = useMemo(
    () => itens.some((i) => !idsIgnorados.has(i.idClockifyTimeEntry)),
    [itens, idsIgnorados],
  )

  const totalMinutosGeral = useMemo(() => {
    const itensValidos = itens.filter((i) => !idsIgnorados.has(i.idClockifyTimeEntry))
    return itensValidos.reduce((acc, i) => acc + (i.minutos || 0), 0)
  }, [itens, idsIgnorados])

  const totalHorasGeral = useMemo(() => {
    const horasInt = Math.floor(totalMinutosGeral / 60)
    const mins = totalMinutosGeral % 60
    return `${String(horasInt).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
  }, [totalMinutosGeral])

  const onBuscar = async () => {
    if (!dataInicio) {
      toast.error('Validação', 'Data inicial é obrigatória')
      return
    }

    if (desenvolvedorIdsSelecionados.length === 0) {
      toast.error('Validação', 'Selecione pelo menos um colaborador')
      return
    }

    setCarregando(true)
    try {
      const resultado = await suporteService.buscarGeracaoEnvio({
        dataInicio,
        dataFim: dataFim || undefined,
        desenvolvedorIds: desenvolvedorIdsSelecionados,
      })

      setItens(resultado.itens || [])
      setIdsIgnorados(new Set())

      if (!resultado.itens || resultado.itens.length === 0)
        toast.warning('Atenção', 'Nenhum apontamento encontrado para os filtros informados')
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao buscar apontamentos')
    } finally {
      setCarregando(false)
    }
  }

  const toggleDevSelecionado = (pessoaId: number) => {
    setDesenvolvedorIdsSelecionados((prev) => {
      if (prev.includes(pessoaId)) return prev.filter((id) => id !== pessoaId)
      return [...prev, pessoaId]
    })
  }

  const toggleIgnorar = (idClockifyTimeEntry: number) => {
    setIdsIgnorados((prev) => {
      const novo = new Set(prev)
      if (novo.has(idClockifyTimeEntry)) novo.delete(idClockifyTimeEntry)
      else novo.add(idClockifyTimeEntry)
      return novo
    })
  }

  const onGerar = async () => {
    setGerando(true)
    try {
      const { id: jobId } = await suporteService.criarJobGeracaoEnvio({
        dataInicio,
        dataFim: dataFim || undefined,
        desenvolvedorIds: desenvolvedorIdsSelecionados,
        idsIgnorados: Array.from(idsIgnorados),
        agruparPorData,
      })

      toast.info('Processando', 'Geração iniciada. Aguardando conclusão...')

      const pollInterval = 2500
      const maxTentativas = 120
      let tentativas = 0
      let status = await suporteService.obterStatusJob(jobId)

      while (status.status !== 'Concluido' && status.status !== 'Falhou' && tentativas < maxTentativas) {
        await new Promise((r) => setTimeout(r, pollInterval))
        tentativas++
        status = await suporteService.obterStatusJob(jobId)
      }

      if (status.status === 'Concluido' && status.temArquivo) {
        const blob = await suporteService.downloadJobGeracaoEnvio(jobId)
        const nomeArquivo = status.nomeArquivo || `suporte_${dataInicio.replaceAll('-', '')}_${(dataFim || dataInicio).replaceAll('-', '')}.xlsx`
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = nomeArquivo
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
        toast.success('Sucesso', 'Planilha gerada com sucesso')
        window.location.reload()
        return
      }

      if (status.status === 'Falhou') {
        toast.error('Erro', status.mensagemErro || 'Falha ao gerar planilha')
        return
      }

      toast.error('Erro', 'Tempo esgotado. Tente novamente.')
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao gerar planilha')
    } finally {
      setGerando(false)
    }
  }

  const onReverterLote = async (loteId: number) => {
    setRevertendoLoteId(loteId)
    try {
      const { id: jobId } = await suporteService.reverterLoteGeracaoEnvioAsync(loteId)
      toast.info('Processando', 'Reversão iniciada. Aguardando conclusão...')

      const pollInterval = 2500
      const maxTentativas = 180
      let tentativas = 0
      let status = await suporteService.obterStatusJob(jobId)

      while (status.status !== 'Concluido' && status.status !== 'Falhou' && tentativas < maxTentativas) {
        await new Promise((r) => setTimeout(r, pollInterval))
        tentativas++
        status = await suporteService.obterStatusJob(jobId)
      }

      if (status.status === 'Concluido') {
        toast.success('Sucesso', 'Lote revertido com sucesso. Desmarcação no Clockify concluída.')
        const atualizados = await suporteService.listarLotesGeracaoEnvio()
        setLotes(atualizados || [])
        return
      }

      if (status.status === 'Falhou') {
        toast.error('Erro', status.mensagemErro || 'Erro ao reverter lote')
        return
      }

      toast.error('Erro', 'Tempo esgotado. Tente novamente.')
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao reverter lote')
    } finally {
      setRevertendoLoteId(null)
    }
  }

  const onDownloadPlanilhaLote = async (loteId: number) => {
    try {
      const blob = await suporteService.downloadPlanilhaLote(loteId)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `suporte_lote_${loteId}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao baixar planilha do lote')
    }
  }

  if (!isAnalista) {
    return (
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm">
        <h1 className="text-xl font-bold text-gray-900 mb-2">Geração para Envio</h1>
        <p className="text-sm text-gray-600">Rotina disponível apenas para analistas.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Geração para Envio</h1>
        <p className="mt-1 text-sm text-gray-600">Filtre e gere a planilha de suporte para envio</p>
      </div>

      {lotes.length > 0 && (
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-theme-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Lotes recentes para reversão</h2>
              <p className="text-xs text-gray-600">Apenas lotes sem faturamento ou nota fiscal.</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left font-medium text-gray-700 py-2 px-3 border-b">Data geração</th>
                  <th className="text-left font-medium text-gray-700 py-2 px-3 border-b">Período</th>
                  <th className="text-right font-medium text-gray-700 py-2 px-3 border-b">Qtde apontamentos</th>
                  <th className="text-right font-medium text-gray-700 py-2 px-3 border-b">Ações</th>
                </tr>
              </thead>
              <tbody>
                {lotes.map((lote) => {
                  const dataGeracao = new Date(lote.dataGeracao).toLocaleString('pt-BR')
                  const periodoInicio = lote.periodoInicio ? new Date(lote.periodoInicio).toLocaleDateString('pt-BR') : '-'
                  const periodoFim = lote.periodoFim ? new Date(lote.periodoFim).toLocaleDateString('pt-BR') : '-'
                  return (
                    <tr key={lote.id} className="hover:bg-gray-50">
                      <td className="py-2 px-3 border-b text-gray-900">{dataGeracao}</td>
                      <td className="py-2 px-3 border-b text-gray-900">
                        {periodoInicio === '-' && periodoFim === '-' ? '-' : `${periodoInicio} até ${periodoFim}`}
                      </td>
                      <td className="py-2 px-3 border-b text-gray-900 text-right">{lote.quantidadeApontamentos}</td>
                      <td className="py-2 px-3 border-b text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => onDownloadPlanilhaLote(lote.id)}
                            className="inline-flex items-center px-3 py-1.5 rounded-lg border border-brand-200 text-xs font-medium text-brand-600 hover:bg-brand-50 transition-colors"
                            title="Baixar planilha gerada"
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Planilha
                          </button>
                          <button
                            type="button"
                            onClick={() => onReverterLote(lote.id)}
                            disabled={revertendoLoteId === lote.id}
                            className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            {revertendoLoteId === lote.id ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : null}
                            Reverter
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {carregandoLotes && (
            <p className="mt-2 text-xs text-gray-500">Carregando lotes...</p>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-fit">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data inicial <span className="text-red-500">*</span>
            </label>
            <InputDate
              value={dataInicio}
              onChange={setDataInicio}
              placeholder="dd/mm/aaaa"
              className="w-44"
            />
          </div>
          <div className="w-fit">
            <label className="block text-sm font-medium text-gray-700 mb-1">Data final (opcional)</label>
            <InputDate
              value={dataFim}
              onChange={setDataFim}
              placeholder="dd/mm/aaaa"
              className="w-44"
            />
          </div>
        </div>

        <div ref={dropdownDevRef} className="relative w-full max-w-xs">
          <label className="block text-sm font-medium text-gray-700 mb-1">Colaboradores</label>
          <button
            type="button"
            onClick={() => setDropdownDevAberto((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white text-left text-sm text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          >
            <span className="truncate">{textoTriggerDesenvolvedores}</span>
            <ChevronDown className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${dropdownDevAberto ? 'rotate-180' : ''}`} />
          </button>
          {dropdownDevAberto && (
            <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-theme-sm">
              <div className="p-2 border-b border-gray-100">
                <input
                  type="text"
                  value={buscaDev}
                  onChange={(e) => setBuscaDev(e.target.value)}
                  placeholder="Pesquisar colaborador"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                {desenvolvedoresFiltrados.map((dev) => (
                  <label
                    key={dev.pessoaId}
                    className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 rounded px-2 py-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={desenvolvedorIdsSelecionados.includes(dev.pessoaId)}
                      onChange={() => toggleDevSelecionado(dev.pessoaId)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span>{dev.nome}</span>
                  </label>
                ))}
                {desenvolvedoresFiltrados.length === 0 && (
                  <p className="text-sm text-gray-500 px-2 py-2">Nenhum colaborador encontrado</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={onBuscar}
            disabled={carregando}
            className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:opacity-50"
          >
            {carregando ? <Loader2 className="w-4 h-4 mr-2 inline animate-spin" /> : <Search className="w-4 h-4 mr-2 inline" />}
            Buscar
          </button>
        </div>
      </div>

      {grupos.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm space-y-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Total geral:</span>{' '}
              {totalMinutosGeral} min ({totalHorasGeral})
            </p>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">Agrupar</span>
              <label className="flex items-center gap-1 text-sm text-gray-700">
                <input
                  type="radio"
                  name="agrupar"
                  value="nao"
                  checked={!agruparPorData}
                  onChange={() => setAgruparPorData(false)}
                  className="h-4 w-4 text-brand-500 border-gray-300"
                />
                <span>Não</span>
              </label>
              <label className="flex items-center gap-1 text-sm text-gray-700">
                <input
                  type="radio"
                  name="agrupar"
                  value="sim"
                  checked={agruparPorData}
                  onChange={() => setAgruparPorData(true)}
                  className="h-4 w-4 text-brand-500 border-gray-300"
                />
                <span>Sim</span>
              </label>
            </div>
          </div>

          {grupos.map((grupo) => (
            <div key={grupo.chave} className="border border-gray-200 rounded-lg p-4">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-3">
                <div className="space-y-1">
                  <p className="text-sm text-gray-600">Ticket</p>
                  <p className="font-semibold text-gray-900">
                    {grupo.nomeSuporte ? `${grupo.numeroTicket} – ${grupo.nomeSuporte}` : grupo.numeroTicket}
                  </p>
                  <p className="text-sm text-gray-600">
                    Responsáveis:{' '}
                    <span className="font-medium text-gray-900">
                      {Array.from(new Set(grupo.itens.map((i) => i.responsavelNome || '-'))).join(', ')}
                    </span>
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">Responsável</th>
                      <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">Data</th>
                      <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">Inicio</th>
                      <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">Termino</th>
                      <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">Descricao</th>
                      <th className="text-right text-sm font-medium text-gray-700 py-2 px-3 border-b">Minutos</th>
                      <th className="text-center text-sm font-medium text-gray-700 py-2 px-3 border-b">Ignorar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupo.itens.map((item) => (
                      <tr key={item.idClockifyTimeEntry} className="hover:bg-gray-50">
                        <td className="py-2 px-3 border-b text-sm text-gray-900">{item.responsavelNome || '-'}</td>
                        <td className="py-2 px-3 border-b text-sm text-gray-900">
                          {item.inicioMarcacao ? new Date(item.inicioMarcacao).toLocaleDateString('pt-BR') : '-'}
                        </td>
                        <td className="py-2 px-3 border-b text-sm text-gray-900">
                          {item.inicioMarcacao ? new Date(item.inicioMarcacao).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td className="py-2 px-3 border-b text-sm text-gray-900">
                          {item.fimMarcacao ? new Date(item.fimMarcacao).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td className="py-2 px-3 border-b text-sm text-gray-900">{item.descricao || '-'}</td>
                        <td className="py-2 px-3 border-b text-sm text-gray-900 text-right">{item.minutos || 0}</td>
                        <td className="py-2 px-3 border-b text-center">
                          <input
                            type="checkbox"
                            checked={idsIgnorados.has(item.idClockifyTimeEntry)}
                            onChange={() => toggleIgnorar(item.idClockifyTimeEntry)}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 w-full md:w-auto md:max-w-md">
                  <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-1 text-xs">
                    <div className="text-xs font-medium text-gray-600">Responsável</div>
                    <div className="text-xs font-medium text-gray-600 text-right">Minutos</div>
                    <div className="text-xs font-medium text-gray-600 text-right">Horas</div>

                    {(resumoPorGrupo.get(grupo.chave)?.porResponsavel || []).map((r) => (
                      <div key={r.nome} className="contents">
                        <div className="text-gray-900">{r.nome}</div>
                        <div className="text-gray-900 text-right">{r.minutos}</div>
                        <div className="text-gray-900 text-right">{r.horas}</div>
                      </div>
                    ))}

                    <div className="col-span-3 border-t border-gray-200 my-1" />

                    <div className="font-semibold text-gray-900">Total (Ticket)</div>
                    <div className="font-semibold text-gray-900 text-right">{resumoPorGrupo.get(grupo.chave)?.totalMinutos || 0}</div>
                    <div className="font-semibold text-gray-900 text-right">{resumoPorGrupo.get(grupo.chave)?.totalHoras || '00:00'}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onGerar}
              disabled={gerando || !temApontamentoParaGerar}
              className="px-6 py-2.5 text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:opacity-50"
            >
              {gerando ? <Loader2 className="w-4 h-4 mr-2 inline animate-spin" /> : <Download className="w-4 h-4 mr-2 inline" />}
              Gerar
            </button>
          </div>
        </div>
      )}

      {grupos.length === 0 && !carregando && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm">
          <div className="text-center py-8">
            <p className="text-sm text-gray-600">Use os filtros e clique em pesquisar para carregar os apontamentos.</p>
          </div>
        </div>
      )}
    </div>
  )
}
